import html2canvas from 'html2canvas'
import { logoPng } from 'images/logos'
import { Html2CanvasOptions, jsPDF as JsPDF } from 'jspdf'
import { findLastIndex, includes } from 'lodash-es'
import { ErrorService } from 'service/ErrorService'
import { logError } from 'utils/logger'

import { uniqueSelector } from './uniqueSelector'

export type SubjectToPdfOptions = {
  tariffsText: string
  tariffsLink: string
  fontSize: number
  fontColor: { red: number; green: number; blue: number }
  offersText: string
  offersLink: string
}

export type HtmlPdfOptions = {
  fileName: string
  footerText?: string
  footerLink?: string
  footerFontSize?: number
  footerFontColor?: { red: number; green: number; blue: number }
  subjectToFooter?: SubjectToPdfOptions
}

export const html2CanvasElem = 'html2canvas-elem'

export class HtmlPdfService {
  // @ts-expect-error TS(2564) FIXME: Property 'container' has no initializer and is not... Remove this comment to see the full error message
  private container: HTMLElement
  // @ts-expect-error TS(2564) FIXME: Property 'header' has no initializer and is not de... Remove this comment to see the full error message
  private header: HTMLElement
  private readonly marginSide = 8
  private readonly footerMarginSide = this.marginSide + 4
  private readonly marginTop = 5 // this margin applies from page 2
  private readonly footerMarginTop = 7
  private readonly ignoreHtml2CanvasAttr = 'data-html2canvas-ignore'
  private readonly footerVerticalSpaceLimit = 10

  setHeader(header: HTMLElement) {
    this.header = header
  }

  setContainer(container: HTMLElement) {
    this.container = container
  }

  async download(content: HTMLElement, options: HtmlPdfOptions, errorService: ErrorService): Promise<void> {
    if (!this.header || !this.container) {
      return
    }
    const scrollPosBefore = this.container.scrollTop
    this.container.scrollTop = 0
    try {
      await this.createPdf(this.header, content, options)
    } catch (error) {
      // @ts-expect-error TS(2345) FIXME: Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
      logError(error)
      errorService.showDialog('An unexpected error occurred while downloading. Please try again.')
    } finally {
      this.container.scrollTop = scrollPosBefore
    }
  }

  private async createPdf(headerElem: HTMLElement, contentElem: HTMLElement, options: HtmlPdfOptions): Promise<void> {
    const pdf = new JsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true }) // portrait mode
    // imageTimeout causing a lot of unhandled error rejections despite all images are resolved, this should be fixed in later versions of html2canvas
    // fixed scale value is required to remove gray shadow that appears in pdf for some screen sizes as default value is window.devicePixelRatio
    const html2canvasOptions: Html2CanvasOptions = {
      logging: false,
      imageTimeout: 0,
      scale: 2,
      // Fixes for svg logo not rendering on chrome.
      allowTaint: true,
      useCORS: true,
    }
    const headerStart = await this.addLogoToPdf(pdf)
    const headerCanvas = await this.html2SVGSafeCanvas(headerElem, html2canvasOptions)
    const headerPageHeight = this.getPageHeight(headerStart, headerCanvas, pdf)
    let headerScale = 1
    if (headerPageHeight > 1) {
      headerScale = 1 / headerPageHeight
    }
    let contentStart = this.addContentToPdf(headerCanvas, headerStart, pdf, headerScale)
    if (headerScale !== 1) {
      // header covers whole page
      pdf.addPage()
      contentStart = this.marginTop
    }

    /**
     * The following logic was added to make sure no elements are cut off half-way
     */
    const elems = this.getElemsToPrint(contentElem)
    let startIdx = 0
    while (true) {
      while (true) {
        // eslint-disable-next-line no-await-in-loop
        const contentCanvas = await this.html2SVGSafeCanvas(contentElem, html2canvasOptions)
        const pageHeight = this.getPageHeight(contentStart, contentCanvas, pdf)
        if (pageHeight <= 1) {
          contentStart = this.addContentToPdf(contentCanvas, contentStart, pdf)
          break
        }
        this.hideMoreElems(pageHeight, elems, startIdx)
      }
      const lastIdx = this.getLastIndexOfUnhiddenElem(elems)
      if (lastIdx === elems.length - 1) {
        break
      }
      this.hide(elems)
      startIdx = lastIdx + 1
      this.unhide(elems, startIdx)
      pdf.addPage()
      contentStart = this.marginTop
    }
    this.unhide(elems) // clean up

    if (options.footerText && options.footerLink) {
      const remainingSpace = pdf.internal.pageSize.height - contentStart
      if (remainingSpace < this.footerVerticalSpaceLimit) {
        pdf.addPage()
        contentStart = this.marginTop
      }
      this.addFooterToPdf(
        pdf,
        contentStart,
        options.footerText,
        options.footerLink,
        // @ts-expect-error TS(2345) FIXME: Argument of type 'number | undefined' is not assig... Remove this comment to see the full error message
        options.footerFontSize,
        options.footerFontColor,
        options.subjectToFooter
      )
    }

    pdf.save(options.fileName)
  }

  /**
   * Hide more elements to fit the elements in one page.
   */
  private hideMoreElems(pageHeight: number, elems: HTMLElement[], startIdx: number): void {
    const lastIdx = this.getLastIndexOfUnhiddenElem(elems)
    const numOfCurrElems = lastIdx - startIdx + 1
    const numOfElemsToHide = Math.ceil((numOfCurrElems * (pageHeight - 1)) / pageHeight)
    for (let i = 0; i < numOfElemsToHide; i++) {
      elems[lastIdx - i].setAttribute(this.ignoreHtml2CanvasAttr, 'true')
      // hide elements for correct sizing
      elems[lastIdx - i].style.display = 'none'
    }
  }

  private getLastIndexOfUnhiddenElem(elems: HTMLElement[]): number {
    return findLastIndex(elems, (elem) => !elem.hasAttribute(this.ignoreHtml2CanvasAttr))
  }

  private unhide(elems: HTMLElement[], startIdx = 0): void {
    for (let i = startIdx; i < elems.length; i++) {
      elems[i].removeAttribute(this.ignoreHtml2CanvasAttr)
      // @ts-expect-error TS(2322) FIXME: Type 'null' is not assignable to type 'string'.
      elems[i].style.display = null
    }
  }

  private getPageHeight(contentStart: number, contentCanvas: HTMLCanvasElement, pdf: JsPDF): number {
    const contentWidth = pdf.internal.pageSize.width - 2 * this.marginSide
    const contentHeight = contentWidth * (contentCanvas.height / contentCanvas.width)
    return (contentStart + contentHeight) / pdf.internal.pageSize.height
  }

  private getElemsToPrint(contentElem: HTMLElement): HTMLElement[] {
    const elems = contentElem.getElementsByClassName(html2CanvasElem) as HTMLCollectionOf<HTMLElement>
    return Array.from(elems)
  }

  private hide(elems: HTMLElement[]): void {
    elems.forEach((elem) => {
      elem.setAttribute(this.ignoreHtml2CanvasAttr, 'true')
      // hide elements for correct sizing
      elem.style.display = 'none'
    })
  }

  private addContentToPdf(contentCanvas: HTMLCanvasElement, contentStart: number, pdf: JsPDF, scale = 1): number {
    const content = contentCanvas.toDataURL('image/jpeg', 0.7)
    const contentWidth = (pdf.internal.pageSize.width - 2 * this.marginSide) * scale
    const contentHeight = contentWidth * (contentCanvas.height / contentCanvas.width) * scale
    pdf.addImage(content, 'jpeg', this.marginSide, contentStart, contentWidth, contentHeight)
    return contentStart + contentHeight
  }

  private async addLogoToPdf(pdf: JsPDF): Promise<number> {
    const logo = await this.getImage(logoPng)
    const logoWidth = 37
    const logoHeight = 9
    const logoPadding = 8
    const pageWidth = pdf.internal.pageSize.width
    const xCenter = (pageWidth - logoWidth) / 2
    pdf.addImage(logo, 'png', xCenter, logoPadding, logoWidth, logoHeight)
    return 2 * logoPadding + logoHeight
  }

  private addFooterToPdf(
    pdf: JsPDF,
    verticalStart: number,
    text: string,
    url: string,
    fontSize: number,
    fontColor: { red: number; green: number; blue: number },
    subjectToFooter: SubjectToPdfOptions
  ) {
    pdf.setFontSize(fontSize)
    pdf.setTextColor(fontColor.red, fontColor.green, fontColor.blue)
    const textRightCorner = pdf.internal.pageSize.width - this.footerMarginSide
    const y = verticalStart + this.footerMarginTop
    pdf.text(text, textRightCorner, y, { align: 'right' })
    const textWidth = pdf.getTextWidth(text)
    const fontSizeMm = fontSize * 0.3528 // 1pt = 0.3528mm; not actually equal to font height but almost
    pdf.link(textRightCorner - textWidth, y - fontSizeMm, textWidth, fontSizeMm, { url })

    // add subject to part to the footer
    this.addDemDetTarrifLink(pdf, y, subjectToFooter)
  }

  private getImage(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => resolve(img)
      img.onerror = () => reject(new Error(`Could not load image from ${url}`))
      img.crossOrigin = 'anonymous'
      img.src = url
    })
  }

  // html2Canvas messes up svg images unless they have fixed width and height
  // that matches its viewbox. But setting the dimensions directly on the
  // element takes away the responsive size that we'd otherwise have.
  // So here we use the `onclone` hook to get all the svg childs of the cloned doc
  // and set their width and height based on viewbox on-fly.
  private async html2SVGSafeCanvas(element: HTMLElement, options: Html2CanvasOptions) {
    return html2canvas(element, {
      ...options,

      onclone(doc) {
        // We are only concerned with the svg elements of the `element` that we
        // want to convert to canvas.
        // We get the unique selector of the element in the original DOM and use
        // it to query the cloned DOM for the same element.
        const selector = uniqueSelector(element)
        let parents: HTMLElement[]

        try {
          parents = Array.from(doc.querySelectorAll<HTMLElement>(selector))
        } catch (ignoreError) {
          // In case of error, we just select all the svg elements from
          // `document.body`
          parents = [document.body]
        }

        parents.forEach((parent) => {
          const svgElements = parent.getElementsByTagName('svg')

          Array.from(svgElements).forEach((svgElement) => {
            const bBox = svgElement.getBBox()
            svgElement.setAttribute('width', String(bBox.width))
            svgElement.setAttribute('height', String(bBox.height))
          })
        })
      },

      // userlane is causing errors during canvas rendering, we need to ignore it
      // @ts-expect-error TS(2322) FIXME: Type '(elementToIgnore: HTMLElement) => boolean' i... Remove this comment to see the full error message
      ignoreElements: (elementToIgnore: HTMLElement) => {
        return includes(['userlane-iframe'], elementToIgnore.id)
      },
    })
  }

  private addDemDetTarrifLink(pdf: JsPDF, verticalStart: number, subjectToFooter: SubjectToPdfOptions) {
    pdf.setFontSize(subjectToFooter.fontSize)
    pdf.textWithLink(subjectToFooter.offersText, this.footerMarginSide, verticalStart, {
      align: 'left',
      url: subjectToFooter.offersLink,
      maxWidth: 120,
    })
    pdf.textWithLink(subjectToFooter.tariffsText, this.footerMarginSide, verticalStart + 5, {
      align: 'left',
      url: subjectToFooter.tariffsLink,
      maxWidth: 120,
    })
    pdf.setTextColor(subjectToFooter.fontColor.red, subjectToFooter.fontColor.green, subjectToFooter.fontColor.blue)
  }
}
